import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Banner,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Control,
  EmptyState,
  Input,
  Label,
  TextArea,
  DescriptionIcon,
  useDocumentTitle,
} from '@forge/common';
import { setRequirements, setSelectedId, useIntakeStore } from './intakeStore';
import { newId } from './intakeUtils';
import type { RequirementVersion } from '@/data/mock-requirements';

const IntakeEditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { requirements } = useIntakeStore();
  const requirement = requirements.find((r) => r.id === id);

  useDocumentTitle(
    `Edit ${requirement?.reference ?? 'request'} · Intake · OneRing`,
  );

  const [title, setTitle] = useState(requirement?.title ?? '');
  const [body, setBody] = useState(requirement?.body ?? '');
  const [summary, setSummary] = useState('');

  if (!requirement) {
    return (
      <div className="p-4">
        <Card>
          <CardContent>
            <EmptyState
              icon={<DescriptionIcon />}
              description="That request could not be found."
            />
            <div className="mt-4 flex justify-center">
              <Button variant="primary" onClick={() => navigate('/intake')}>
                Back to Intake
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const willRevert = requirement.status !== 'draft';
  const canSubmit = Boolean(title.trim() && body.trim());

  const handleSave = (): void => {
    const now = new Date().toISOString();
    const nextVersion = requirement.currentVersion + 1;
    const causedRevert = requirement.status !== 'draft';
    const trimmedTitle = title.trim();

    setRequirements((prev) =>
      prev.map((req) => {
        if (req.id !== requirement.id) return req;
        const version: RequirementVersion = {
          version: nextVersion,
          statusAtChange: req.status,
          title: trimmedTitle,
          body,
          changeSummary: summary.trim() || undefined,
          changedBy: req.clientName,
          changedByRole: 'client',
          changedAt: now,
          causedRevert,
        };
        const events = [...req.events];
        if (causedRevert) {
          events.push({
            id: newId(),
            type: 'content_reverted',
            fromStatus: req.status,
            toStatus: 'in_account_manager',
            actor: req.clientName,
            actorRole: 'client',
            createdAt: now,
            body: `Edited v${nextVersion}${
              summary.trim() ? ` — ${summary.trim()}` : ''
            }. Sent back for re-review.`,
          });
        }
        return {
          ...req,
          title: trimmedTitle,
          body,
          currentVersion: nextVersion,
          status: causedRevert ? 'in_account_manager' : req.status,
          updatedAt: now,
          versions: [...req.versions, version],
          events,
        };
      }),
    );
    setSelectedId(requirement.id);
    navigate(`/intake/${requirement.id}`);
  };

  return (
    <div className="space-y-4 p-4">
      <Breadcrumbs
        crumbs={[
          { id: 'intake', label: 'Intake', href: '/intake' },
          { id: 'edit', label: `Edit ${requirement.reference}`, noAction: true },
        ]}
        onChange={({ id: crumbId }) => {
          if (crumbId === 'intake') navigate('/intake');
        }}
      />

      <div className="min-w-0">
        <h2 className="text-xl font-bold">Edit {requirement.reference}</h2>
        <p className="mt-1 max-w-2xl text-sm text-color-tertiary">
          Update the request details. Each save is recorded as a new version so
          every change is traceable.
        </p>
      </div>

      <Card className="max-w-3xl">
        <CardContent className="space-y-4">
          {willRevert && (
            <Banner
              variant="inline"
              type="warning"
              title="Re-review required"
              description="Editing after submission records a new version and sends this request back to Account Manager Review for re-review."
              canClose={false}
            />
          )}
          <Control hideSpacing="bottom">
            <Label htmlFor="edit-title">Title</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Control>
          <Control hideSpacing="bottom">
            <Label htmlFor="edit-body">Request details</Label>
            <TextArea
              id="edit-body"
              rows={14}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </Control>
          <Control hideSpacing="bottom">
            <Label htmlFor="edit-summary">What changed? (optional)</Label>
            <Input
              id="edit-summary"
              placeholder="e.g., Added retention rule and failure alert"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
            />
          </Control>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4 dark:border-gray-800">
            <Button variant="secondary" onClick={() => navigate(`/intake/${requirement.id}`)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={!canSubmit} onClick={handleSave}>
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IntakeEditPage;
